import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { useContext, useEffect } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { Box, Typography, useTheme } from '@mui/material';
import AdminItemContainer from 'pages/adminPages/components/adminItemContainer';
import ColorIndicator from 'pages/adminPages/components/colorIndicator';
import map from 'lodash-es/map';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import EmptyList from 'shared/lib/components/plugs/emptyList';
import ListSkeleton from 'shared/lib/components/plugs/listSkeleton';
import SearchTextField from 'shared/lib/components/controls/textFields/searchTextField';
import filter from 'lodash-es/filter';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import BlockHeader from 'pages/components/blockHeader';
import IconTrash from 'shared/icons/trash';
import { AdminInnerProps } from './adminUsersInner';
import WarningModal from 'shared/lib/components/modals/warningModal';

export default observer(function AdminRequestsInner({
  search,
  setSearch,
}: AdminInnerProps) {
  const { mobxStore } = useContext(MobxContext);
  const {
    getFontSize,
    spacings: { sp },
  } = useContext(UiContext);

  const [declineId, setDeclineId] = useState<string | null>(null);

  useEffect(() => {
    mobxStore.adminRequestsStore.fetch();
  }, [mobxStore.adminRequestsStore]);

  const f16 = getFontSize('f16');

  const filtered = useMemo(
    () =>
      filter(
        mobxStore.adminRequestsStore.requests,
        (user) =>
          user.name.toLowerCase().includes(search.toLowerCase()) ||
          user.email.toLowerCase().includes(search.toLowerCase())
      ),
    [mobxStore.adminRequestsStore.requests, search]
  );

  const { isLessLaptop, isLessTablet, isLessDesktopSmall } =
    useBooleanScreens();

  const requestEl = (
    <Typography
      variant="f18"
      color="text.secondary"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
        width: 1,
        height: 1,
        pr: sp.s80,
        borderRight: isLessLaptop ? 0 : 1,
        borderColor: 'custom.borders',
        ...(isLessTablet && { justifyContent: 'center' }),
      }}
    >
      {isLessLaptop ? 'Заявка' : 'Заявка на регистрацию'}
    </Typography>
  );

  return (
    <>
      <BlockHeader text="Заявки"></BlockHeader>

      {filtered.length ? (
        <Box
          sx={{
            display: isLessTablet ? 'flex' : 'grid',
            flexDirection: 'column',
            gridTemplateColumns: 'min-content 4fr 3fr min-content',
            rowGap: 2,
            gridAutoFlow: 'row',
            gridAutoRows: '1fr',
          }}
        >
          {map(filtered, (request) => {
            return (
              <Box
                sx={{
                  'display': isLessTablet ? 'flex' : 'contents',
                  'flexDirection': 'column',
                  'alignItems': 'center',
                  'width': 1,
                  '& > *': { width: 1 },
                }}
                key={request.id}
              >
                <ColorIndicator
                  isHorizontal={isLessTablet}
                  color="custom.adminRequest"
                ></ColorIndicator>
                <AdminItemContainer
                  sx={{
                    px: isLessLaptop ? 4 : 17,
                    justifyContent: isLessTablet ? 'center' : 'start',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      textAlign: isLessTablet ? 'center' : 'left',
                    }}
                  >
                    <Typography variant="f18b">{request.name}</Typography>
                    <Typography variant="f14">{request.email}</Typography>
                  </Box>
                </AdminItemContainer>
                <AdminItemContainer
                  sx={{
                    px: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: 1,
                      borderLeft: isLessTablet ? 0 : 1,
                      borderColor: 'custom.borders',
                    }}
                  >
                    <ButtonsContainer
                      sx={{
                        flexWrap: 'nowrap',
                        width: 1,
                        gap: sp.s20,
                        justifyContent: 'center',
                        px: isLessDesktopSmall ? 4 : 17,
                        button: {
                          height: '47px !important',
                          fontSize: '16px !important',
                          maxWidth: 166,
                        },
                      }}
                    >
                      <SecondaryButton onClick={() => setDeclineId(request.id)}>
                        Отклонить
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={async () => {
                          await mobxStore.adminRequestsStore.accept(request.id);
                          await mobxStore.adminUsersStore.fetch();
                        }}
                      >
                        Принять
                      </PrimaryButton>
                    </ButtonsContainer>
                  </Box>
                </AdminItemContainer>
                <AdminItemContainer
                  sx={{
                    justifyContent: isLessTablet ? 'center' : 'start',
                    display: isLessTablet ? 'none' : 'flex',
                    borderTopRightRadius: 10,
                    borderBottomRightRadius: 10,
                  }}
                >
                  <IconTrash
                    style={{
                      visibility: 'hidden', // для grid сетки
                    }}
                    sizeKey="24x24"
                  ></IconTrash>
                </AdminItemContainer>
              </Box>
            );
          })}
        </Box>
      ) : mobxStore.adminRequestsStore.requests?.length ? (
        <EmptyList text="Совпадений нет"></EmptyList>
      ) : mobxStore.adminRequestsStore.requests ? (
        <EmptyList text="Нет новых заявок"></EmptyList>
      ) : (
        <ListSkeleton></ListSkeleton>
      )}
      <WarningModal
        isOpen={!!declineId}
        mainText="Вы уверены, что хотите отклонить заявку?"
        secondText="После отклонения заявки, запись о ней будет удалена из системы навсегда"
        onCancel={() => setDeclineId(null)}
        onContinue={async () => {
          if (declineId) {
            try {
              await mobxStore.adminRequestsStore.decline(declineId);
              setDeclineId(null);
            } catch (e) {
              console.error(e);
              setDeclineId(null);
              mobxStore.notificationsStore.errorMessage =
                'Не удалось отклонить заявку';
            }
          }
        }}
        continueText="Да, отклонить"
        cancelText="Отмена"
      />
    </>
  );
});
